import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../../../context/AuthContext';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { FiArrowLeft, FiDownload } from 'react-icons/fi';
import jsPDF from 'jspdf';
import domtoimage from 'dom-to-image';
import TitlePage from './components/TitlePage';
import ImportantPages from './components/ImportantPages';
import YandexMetrics from './components/YandexMetrics';
import SeoPositions from './components/SeoPositions';
import Linkbuilding from './components/Linkbuilding';
import SeoTasks from './components/SeoTasks';

const SiteReport = () => {
  const { currentLang, token, API_URL } = useAuth();
  const { siteId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const reportRef = useRef(null);
  
  // Получаем параметры из URL
  const dateFrom = searchParams.get('dateFrom');
  const dateTo = searchParams.get('dateTo');
  
  // Состояние для сайта и данных отчета
  const [site, setSite] = useState(null);
  const [siteLoading, setSiteLoading] = useState(false);
  const [siteError, setSiteError] = useState('');
  const [exporting, setExporting] = useState(false);

  // Состояние для данных отчета
  const [reportData, setReportData] = useState({
    sitemaps_link: 0,
    pages_count: 0,
    tasks_count: 0
  });

  // Новые состояния для отчётных данных
  const [keywordsCount, setKeywordsCount] = useState(0);
  const [linksInPeriod, setLinksInPeriod] = useState(0);
  const [top10Count, setTop10Count] = useState(0);
  const [top10Google, setTop10Google] = useState(0);
  const [top10Yandex, setTop10Yandex] = useState(0);
  const [top5Google, setTop5Google] = useState(0);
  const [top5Yandex, setTop5Yandex] = useState(0);
  const [tasksCount, setTasksCount] = useState(0);

  // Получаем данные для ключевых слов и ТОП-10 из SeoPositions
  const handleSeoPositionsStats = React.useCallback((stats) => {
    setKeywordsCount(stats.keywordsCount || 0);
    setTop10Count(stats.top10Count || 0);
    setTop10Google(stats.top10_google || 0);
    setTop10Yandex(stats.top10_yandex || 0);
    setTop5Google(stats.top5_google || 0);
    setTop5Yandex(stats.top5_yandex || 0);
  }, []);

  // Получаем данные о ссылках за период из Linkbuilding
  const handleLinkbuildingStats = React.useCallback((stats) => {
    setLinksInPeriod(stats.linksInPeriod || 0);
  }, []);

  // Получаем количество задач из SeoTasks
  const handleSeoTasksStats = React.useCallback((stats) => {
    setTasksCount(stats.tasksCount || 0);
  }, []);

  // Загрузка информации о сайте и данных отчета
  useEffect(() => {
    if (siteId) {
      fetchSiteData();
    }
  }, [siteId]);

  const fetchSiteData = async () => {
    try {
      setSiteLoading(true);
      setSiteError('');
      
      const response = await fetch(`${API_URL}${currentLang}/seo/sites/${siteId}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        credentials: 'include',
      });

      const data = await response.json();

      if (response.ok && data.success) {
        setSite(data.site);
        
        // Извлекаем данные для отчета из ответа API
        const siteData = data.site;
        setReportData({
          sitemaps_link: siteData.sitemaps_link || 0,
          pages_count: siteData.pages_count || 0,
          tasks_count: 0 // Будет обновлено из SeoTasks
        });
      } else {
        throw new Error(data.message || 'Ошибка загрузки сайта');
      }
    } catch (error) {
      setSiteError(error.message);
    } finally {
      setSiteLoading(false);
    }
  };

  const handleBackToReports = () => {
    navigate('/seo/reports');
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Не указана';
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return 'Неверная дата';
    return date.toLocaleDateString('ru-RU');
  };

  const exportToPDF = async () => {
    if (!reportRef.current) return;
    try {
      setExporting(true);
      const element = reportRef.current;
      // Используем dom-to-image для создания изображения
      const imgData = await domtoimage.toJpeg(element, { quality: 0.9, bgcolor: '#ffffff' });
      const pdf = new jsPDF('p', 'mm', 'a4');
      const img = new window.Image();
      img.src = imgData;
      img.onload = function () {
        /*
        const imgWidth = 210; // A4 ширина в мм
        const pageHeight = 295; // A4 высота в мм
        const pxPerMm = img.width / imgWidth; // px на мм
        const pageHeightPx = pageHeight * pxPerMm; // высота страницы в px

        let position = 0;
        let pageNum = 0;
        while (position < img.height) {
          // Создаём временный canvas для текущей страницы
          const canvas = document.createElement('canvas');
          canvas.width = img.width;
          canvas.height = Math.min(pageHeightPx, img.height - position);
          const ctx = canvas.getContext('2d');
          ctx.drawImage(
            img,
            0, position, img.width, canvas.height, // источник
            0, 0, img.width, canvas.height        // назначение
          );
          const pageData = canvas.toDataURL('image/jpeg', 0.9);
          if (pageNum > 0) pdf.addPage();
          pdf.addImage(pageData, 'JPEG', 0, 0, imgWidth, (canvas.height / pxPerMm));
          position += pageHeightPx;
          pageNum++;
        }
        const fileName = `seo_report_${site?.dns?.replace(/[^a-zA-Z0-9]/g, '_')}_${formatDate(dateFrom)}_${formatDate(dateTo)}.pdf`;
        pdf.save(fileName);
        setExporting(false);
        */
        const imgWidth = 210; // мм (A4)
        // Высота страницы в мм по пропорции картинки
        const imgHeight = (img.height * imgWidth) / img.width;
        // Создаём PDF с нужной высотой
        const pdf = new jsPDF('p', 'mm', [imgWidth, imgHeight]);
        pdf.addImage(imgData, 'JPEG', 0, 0, imgWidth, imgHeight);
        const fileName = `seo_report_${site?.dns?.replace(/[^a-zA-Z0-9]/g, '_')}_${formatDate(dateFrom)}_${formatDate(dateTo)}.pdf`;
        pdf.save(fileName);
        setExporting(false);
      };
    } catch (error) {
      alert('Ошибка при экспорте отчета в PDF');
      setExporting(false);
    }
  };

  // Показываем загрузку сайта
  if (siteLoading) {
    return (
      <div className="mt-8 sm:mt-12 mb-4 sm:mb-8 flex justify-center items-center">
        <div className="animate-spin rounded-full h-6 w-6 sm:h-8 sm:w-8 border-b-2 border-blue-500"></div>
        <p className="ml-2 sm:ml-3 text-gray-600 text-sm sm:text-base">Загрузка данных отчета...</p>
      </div>
    );
  }

  // Показываем ошибку сайта
  if (siteError) {
    return (
      <div className="mt-8 sm:mt-12 mb-4 sm:mb-8">
        <div className="bg-red-100 border border-red-400 text-red-700 px-3 sm:px-4 py-2 sm:py-3 rounded text-sm">
          Ошибка загрузки данных: {siteError}
        </div>
        <button
          onClick={handleBackToReports}
          className="mt-3 sm:mt-4 flex items-center gap-2 px-3 sm:px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors text-sm sm:text-base"
        >
          <FiArrowLeft className="h-4 w-4" />
          Назад к отчетам
        </button>
      </div>
    );
  }

  return (
    <div className="mt-8 sm:mt-12 mb-4 sm:mb-8 flex flex-col gap-6 sm:gap-12">
      {/* Панель управления */}
      <div className="bg-white rounded-lg sm:rounded-xl shadow-lg sm:shadow-xl dark:bg-navy-800">
        <div className="bg-gradient-to-r from-gray-100 to-blue-100 rounded-t-lg sm:rounded-t-xl p-4 sm:p-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-0">
            <div>
              <h1 className="text-lg sm:text-xl font-bold text-blue-500">
                SEO Отчет
              </h1>
              <div className="flex items-center mt-1 sm:mt-2">
                <img src={`https://www.google.com/s2/favicons?domain=${site?.dns || ''}`} alt="favicon" className="w-3 h-3 sm:w-4 sm:h-4 mr-1 sm:mr-2" />
                <span className="text-gray-900 text-sm sm:text-base truncate">{site?.dns || ''}</span>
              </div>
              <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                Период: {formatDate(dateFrom)} - {formatDate(dateTo)}
              </p>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={exportToPDF}
                disabled={exporting}
                className="flex items-center gap-2 px-3 sm:px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors disabled:opacity-50 text-sm sm:text-base"
              >
                {exporting ? (
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                ) : (
                  <FiDownload className="h-4 w-4" />
                )}
                <span className="hidden sm:inline">Экспорт PDF</span>
                <span className="sm:hidden">PDF</span>
              </button>
              <button
                onClick={handleBackToReports}
                className="flex items-center gap-2 px-3 sm:px-4 py-2 bg-white/20 text-blue-500 rounded-lg hover:bg-white/30 transition-colors border border-blue-500 text-sm sm:text-base"
              >
                <FiArrowLeft className="h-4 w-4" />
                <span className="hidden sm:inline">Назад к отчетам</span>
                <span className="sm:hidden">Назад</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Отчет для экспорта */}
      <div ref={reportRef} className="bg-white rounded-lg sm:rounded-xl shadow-lg sm:shadow-xl dark:bg-navy-800">
        {/* Титульная страница */}
        <TitlePage 
          site={site} 
          dateFrom={dateFrom} 
          dateTo={dateTo} 
          reportData={{
            ...reportData,
            tasks_count: tasksCount,
            keywords_count: keywordsCount,
            links_in_period: linksInPeriod,
            top10_count: top10Count,
            top10_google: top10Google,
            top10_yandex: top10Yandex,
            top5_google: top5Google,
            top5_yandex: top5Yandex,
          }} 
        />
        
        {/* Важные страницы сайта */}
        <ImportantPages 
          siteId={siteId} 
          dateFrom={dateFrom} 
          dateTo={dateTo} 
        />

        {/* Аналитика Яндекс.Метрики */}
        <YandexMetrics 
          siteId={siteId}
          ymetricaId={site?.ymetrica}
          dateFrom={dateFrom}
          dateTo={dateTo}
          currentLang={currentLang}
        />

        {/* SEO Позиции */}
        <SeoPositions 
          siteId={siteId}
          dateFrom={dateFrom}
          dateTo={dateTo}
          currentLang={currentLang}
          onStats={handleSeoPositionsStats}
        />

        {/* Линкбилдинг / внешняя оптимизация */}
        <Linkbuilding 
          site={site}
          siteId={siteId}
          dateFrom={dateFrom}
          dateTo={dateTo}
          currentLang={currentLang}
          onStats={handleLinkbuildingStats}
        />

        {/* SEO Задачи за период */}
        <SeoTasks
          siteId={siteId}
          dateFrom={dateFrom}
          dateTo={dateTo}
          currentLang={currentLang}
          onStats={handleSeoTasksStats}
        />

        {/* Нижняя часть */}
        <div className="p-8 sm:p-12 bg-white dark:bg-navy-800">
          <div className="mt-12 pt-8 border-t border-gray-300 dark:border-gray-600">
            <div className="flex justify-between items-center">
              <div className="text-sm text-gray-500 dark:text-gray-400">
                <p>Отчет сгенерирован автоматически системой SEO CRM</p>
                <p className="mt-1">Веб-студия LIUKIT © 2025</p>
              </div>
              <div className="text-right text-sm text-gray-500 dark:text-gray-400">
                <p>Период анализа</p>
                <p className="font-semibold">{formatDate(dateFrom)} - {formatDate(dateTo)}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SiteReport; 